/**
 * Cargos recurrentes (puro): gastos que se repiten cada mes en la misma cuenta con el mismo concepto (hipotecas,
 * comunidad, seguros, suscripciones…). Sirve para avisar de un cambio de cuota, para saber qué falta por pasar
 * en cada cuenta este mes y, más adelante, para "dónde recortar".
 *
 * Cada préstamo es un grupo distinto aunque compartan categoría: el banco usa un concepto diferente para cada uno
 * (normalmente con su número), así que dos hipotecas salen como dos cargos.
 */
import { round2 } from './money.js';

export interface RecurringInput { id: string; date: string; concept: string; amount: number; attributable: number; account: string; category: string }

export interface RecurringCharge {
  key: string;
  account: string;
  category: string;
  /** Nombre para mostrar: la categoría y, si hace falta distinguirlo, el comercio o los últimos dígitos del préstamo. */
  label: string;
  /** Día del mes en que suele pasar (mediana). */
  day: number;
  /** Importe total cobrado el último mes con cargo y el anterior (en negativo, como en el banco). */
  last: number;
  previous: number | null;
  lastMonth: string;
  /** Lo que te corresponde del último cargo (p. ej. la mitad si es compartido) / importe del banco. */
  share: number;
  /** Meses distintos con cargo en el último año. */
  months: number;
  /** Ya ha pasado en el mes de referencia. */
  chargedThisMonth: boolean;
  /** Importes de los últimos meses cerrados con cargo (el último al final). */
  history: number[];
  /** Máximo de cargos en un mismo mes: 1 en un recibo o una suscripción; varios en compras con tarjeta. */
  maxPerMonth: number;
}

const MONTH_WORDS = /\b(ENE(RO)?|FEB(RERO)?|MAR(ZO)?|ABR(IL)?|MAY(O)?|JUN(IO)?|JUL(IO)?|AGO(STO)?|SEP(TIEMBRE)?|SEPT|OCT(UBRE)?|NOV(IEMBRE)?|DIC(IEMBRE)?)\b/g;

/** Firma del concepto: sin fechas, meses ni números cortos que cambian cada mes; los números largos (préstamo, tarjeta) se quedan. */
export function conceptSignature(concept: string): string {
  return concept.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()
    .replace(/\b\d{1,4}[/.-]\d{1,2}([/.-]\d{2,4})?\b/g, ' ')   // fechas 03/2026, 2026-03, 01.03.26
    .replace(MONTH_WORDS, ' ')
    .replace(/\b\d{1,3}\b/g, ' ')                                // números cortos (recibo nº 3, día 5)
    .replace(/[^A-Z0-9]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
}

const median = (xs: number[]) => { const s = [...xs].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length ? (s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2) : 0; };
const prevMonthOf = (m: string) => { const [y, mo] = m.split('-').map(Number) as [number, number]; return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`; };

/**
 * Gastos que se repiten: al menos 3 meses distintos de los últimos 12 y en al menos 2 de los 3 meses cerrados
 * anteriores a `month` (así un cargo que ya no existe deja de contar).
 */
export function recurringCharges(items: RecurringInput[], month: string): RecurringCharge[] {
  const from = `${Number(month.slice(0, 4)) - 1}${month.slice(4)}`;
  const recent = [prevMonthOf(month), prevMonthOf(prevMonthOf(month)), prevMonthOf(prevMonthOf(prevMonthOf(month)))];
  const groups = new Map<string, RecurringInput[]>();
  for (const it of items) {
    if (it.amount >= 0 || !it.date || it.date.slice(0, 7) < from || it.date.slice(0, 7) > month) continue;
    const sig = conceptSignature(it.concept);
    if (!sig) continue;
    const k = `${it.account}|${sig}`;
    groups.set(k, [...(groups.get(k) ?? []), it]);
  }
  const out: RecurringCharge[] = [];
  groups.forEach((list, key) => {
    const byMonth = new Map<string, RecurringInput[]>();
    list.forEach(x => byMonth.set(x.date.slice(0, 7), [...(byMonth.get(x.date.slice(0, 7)) ?? []), x]));
    const closed = [...byMonth.keys()].filter(m => m < month).sort();
    if (closed.length < 3 || recent.filter(m => byMonth.has(m)).length < 2) return;
    const lastMonth = closed[closed.length - 1]!, prevM = closed[closed.length - 2];
    const sum = (m: string) => round2(byMonth.get(m)!.reduce((t, x) => t + x.amount, 0));
    const lastItems = byMonth.get(lastMonth)!;
    const last = sum(lastMonth);
    const attributable = lastItems.reduce((t, x) => t + x.attributable, 0);
    const categories = list.map(x => x.category);
    const category = categories.sort((a, b) => categories.filter(c => c === b).length - categories.filter(c => c === a).length)[0] ?? '';
    out.push({
      key, account: list[0]!.account, category, label: category,
      day: Math.round(median(list.map(x => Number(x.date.slice(8, 10))))),
      last, previous: prevM ? sum(prevM) : null, lastMonth,
      share: last ? Math.round(attributable / last * 100) / 100 : 1,
      months: byMonth.size, chargedThisMonth: byMonth.has(month), history: closed.slice(-4).map(sum),
      maxPerMonth: Math.max(...closed.map(m => byMonth.get(m)!.length)),
    });
  });
  // Nombres: si en una cuenta hay varios de la misma categoría, se distinguen por los dígitos del préstamo o el comercio
  out.forEach(r => {
    const twins = out.filter(o => o.account === r.account && o.category === r.category);
    if (twins.length < 2 && r.category) return;
    const sig = r.key.split('|')[1]!;
    const digits = sig.match(/\d{4,}/g)?.pop();
    r.label = `${r.category || 'Sin categoría'} · ${digits ? `…${digits.slice(-4)}` : sig.split(' ').slice(0, 2).join(' ').toLowerCase()}`;
  });
  return out.sort((a, b) => a.account.localeCompare(b.account) || a.day - b.day);
}

/** Cambio de importe del último cargo frente al anterior (euríbor, subida de precio…). null si no ha cambiado. */
export function chargeChange(r: RecurringCharge): { from: number; to: number; diff: number; yearly: number } | null {
  if (r.previous === null) return null;
  // Solo cargos de importe fijo: los meses anteriores al último iguales (±1 %). Un supermercado no "cambia de cuota".
  const before = r.history.slice(0, -1).map(Math.abs);
  if (before.length < 2 || Math.max(...before) - Math.min(...before) > Math.max(0.5, Math.max(...before) * 0.01)) return null;
  const diff = round2(Math.abs(r.last) - Math.abs(r.previous));
  if (Math.abs(diff) < 1 || Math.abs(diff) < Math.abs(r.previous) * 0.01) return null;
  return { from: Math.abs(r.previous), to: Math.abs(r.last), diff, yearly: round2(diff * 12) };
}
